<template>
  <Section
    :id="id"
    :header="t('common.basicInfo')"
    class="shipment-basic-info-section p-0"
    data-class="shipment-basic-info-section-data"
  >
    <SwcMessage v-if="internalValue.swc" :text="t('swc.swcShipmentInfo')" />
    <CustomerRoutedMessage
      v-else-if="internalValue.customerRouted"
      :text="t('customerRouted.customerRoutedShipmentInfo')"
    />

    <ShipmentServiceIncidentMessage
      v-if="hasIncidents"
      :service-incident-types="serviceIncidentTypes"
      :shipment-service-incidents="shipmentServiceIncidents"
      @scroll-trigger="emit('scroll-trigger', $event)"
    />

    <div class="row">
      <div class="shipment-basic-info-cell">
        <KeyValuePair
          v-if="internalValue.mode"
          :label="t('shipments.modes')"
          :value="
            internalValue.mode
              ? translateEnum(internalValue.mode, 'Mode')
              : undefined
          "
          uppercase-label
        />
        <PopupDropdown
          v-else
          v-model="internalValue.requestedMode"
          :disabled="!editable"
          :label="t('common.requestedMode')"
          :options="availableModes"
          option-label="label"
          option-value="value"
          placeholder="-"
          clearable
        />
      </div>
      <div class="shipment-basic-info-cell">
        <PopupDropdown
          v-model="internalValue.equipmentType"
          :disabled="!editable"
          :label="
            t('common.paramTypes', { param: t('referenceData.equipment') })
          "
          :loading="clientEquipmentTypes.loading || carrier.loading"
          :options="availableEquipmentTypes"
          data-key="naturalId"
          option-label="name"
          placeholder="-"
          clearable
        />
      </div>

      <div class="shipment-basic-info-cell">
        <KeyValuePair
          :label="t('rateShop.service')"
          :value="titleCase(internalValue.service)"
          uppercase-label
        />
      </div>
      <div class="shipment-basic-info-cell">
        <KeyValuePair
          :label="t('common.expedited')"
          :value="renderBoolean(isExpedited)"
          uppercase-label
        />
      </div>
      <div class="shipment-basic-info-cell">
        <KeyValuePair
          :label="t('lineItems.hazardous')"
          :value="renderBoolean(internalValue.isHazmat)"
          uppercase-label
        />
      </div>
    </div>
    <div class="row">
      <div class="shipment-basic-info-cell">
        <PopupSwitch
          v-model="internalValue.hot"
          :label="t('common.hot')"
          :disabled="!editable"
        />
      </div>
      <div class="shipment-basic-info-cell">
        <KeyValuePair
          :label="t('shipments.stops', 2)"
          :value="internalValue.stops.length"
          uppercase-label
        />
      </div>
      <div class="shipment-basic-info-cell">
        <template
          v-if="$hasAuthority('shipmentFinancials', 'readShipmentLedger')"
        >
          <KeyValuePair
            v-if="isActualCost"
            v-tooltip.bottom="{
              value: t('shipmentFinancials.nonPayable.clientCostTooltip'),
              disabled: isPayable,
            }"
            :label="t('shipmentFinancials.cost')"
            :value="`${renderMoney(actualCost)} ${!isPayable ? '*' : ''}`"
            uppercase-label
          />
          <KeyValuePair
            v-else
            :label="
              t('shipmentFinancials.paramEstimate', {
                param: t('shipmentFinancials.cost'),
              })
            "
            :value="renderMoney(costEstimate)"
            uppercase-label
          />
        </template>
        <template v-else>
          <KeyValuePair
            :label="
              t('shipmentFinancials.paramEstimate', {
                param: t('shipmentFinancials.cost'),
              })
            "
            value="---"
            uppercase-label
          />
        </template>
      </div>
      <div class="shipment-basic-info-cell">
        <PopupDropdown
          v-model="internalValue.direction"
          :disabled="!editable"
          :label="t('shipments.directions')"
          :options="availableDirections"
          :validation-rules="{
            required: required(t('shipments.directions')),
          }"
          option-label="label"
          option-value="value"
          placeholder="-"
        />
      </div>
      <div class="shipment-basic-info-cell">
        <PopupDropdown
          v-model="internalValue.billingTerms"
          :disabled="!editable"
          :label="t('shipments.billingTerms')"
          :options="availableBillingTerms"
          :validation-rules="{
            required: required(t('shipments.billingTerms')),
          }"
          option-label="label"
          option-value="value"
          placeholder="-"
        />
      </div>
    </div>
  </Section>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { usePrimeVue } from 'primevue/config';
import { computed, onMounted, PropType, ref, Ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import {
  required,
  useModelValue,
  useToast,
  useTooltip,
} from '@rygen/rygen-ui/composables';
import {
  lowerCase,
  mapEnumToTranslatedDropdownValues,
  renderBoolean,
  renderMoney,
  titleCase,
  translateEnum,
} from '@rygen/rygen-ui/i18n';
import {
  BillingTerms,
  Direction,
  Loadable,
  Mode,
  ServiceIncidentType,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { useShipmentFinancials } from '@/composables';
import {
  Carrier,
  Shipment,
  ShipmentServiceIncident,
  WellKnownService,
} from '@/model';
import { useEquipmentTypeStore } from '@/stores/EquipmentType';

import ShipmentServiceIncidentMessage from '@/components/shipment/ShipmentServiceIncidentMessage.vue';
import CustomerRoutedMessage from '@/components/ui/CustomerRoutedMessage.vue';
import PopupDropdown from '@rygen/rygen-ui/components/popup/PopupDropdown.vue';
import PopupSwitch from '@rygen/rygen-ui/components/popup/PopupSwitch.vue';
import SwcMessage from '@rygen/rygen-ui/components/swc/SwcMessage.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Shipment,
    default: () => new Shipment(),
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
  serviceIncidentTypes: {
    type: Array as PropType<ServiceIncidentType[]>,
    required: true,
  },
  shipmentServiceIncidents: {
    type: Array as PropType<ShipmentServiceIncident[]>,
    default: () => [],
  },
});
const {
  id,
  modelValue,
  editable,
  serviceIncidentTypes,
  shipmentServiceIncidents,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: Shipment): void;
  (event: 'scroll-trigger', serviceIncidentId: number): void;
}>();

const {
  equipmentTypes: clientEquipmentTypes,
  loadEquipmentTypes: loadClientEquipmentTypes,
} = useEquipmentTypeStore();
const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<Shipment>) => new Shipment(opts),
});
const { isPayable, isActualCost, actualCost, costEstimate } =
  useShipmentFinancials(internalValue);
const toast = useToast();
const vTooltip = useTooltip();

const availableDirections = mapEnumToTranslatedDropdownValues(
  Direction,
  'Direction',
);

const availableBillingTerms = mapEnumToTranslatedDropdownValues(
  BillingTerms,
  'BillingTerms',
);

const availableModes = mapEnumToTranslatedDropdownValues(Mode, 'Mode');

const carrier: Ref<Loadable<Carrier>> = ref({
  loading: false,
});

const hasIncidents = computed(() => {
  return shipmentServiceIncidents.value.length > 0;
});

const isExpedited = computed(() => {
  const internalService = lowerCase(internalValue.value.service);
  return internalService === lowerCase(WellKnownService.EXPEDITED);
});

const carrierEquipmentTypes = computed(() => {
  if (!carrier.value.data) {
    return;
  }

  return carrier.value.data?.equipmentTypes ?? [];
});

const availableEquipmentTypes = computed(() => {
  const types = carrierEquipmentTypes.value?.length
    ? carrierEquipmentTypes.value
    : (clientEquipmentTypes.value.data ?? []);

  // This is a hack to make sure the selected equipment type is always displayed
  if (internalValue.value.equipmentType) {
    const hasSelectedType = types?.some(
      (type) => type.naturalId === internalValue.value.equipmentType?.naturalId,
    );
    if (!hasSelectedType) {
      types.push(internalValue.value.equipmentType);
    }
  }

  return types;
});

const loadCarrier = async (id: number) => {
  if (!authorization.hasAuthority('carrier', 'read')) {
    return;
  }

  try {
    carrier.value.loading = true;
    carrier.value.data = await api.carrier.findById(id);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    carrier.value.error = e;
    console.error('Error loading carrier', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('carriers.carriers'),
      }),
      error: e,
    });
  } finally {
    carrier.value.loading = false;
  }
};

watch(
  () => internalValue.value.carrierId,
  (newVal: number | undefined) => {
    if (newVal) {
      loadCarrier(newVal);
    }
  },
  { deep: true, immediate: true },
);

onMounted(() => {
  if (editable.value) {
    loadClientEquipmentTypes();
  }
});

defineExpose({
  $primevue: usePrimeVue(),
});
</script>

<style>
.shipment-basic-info-section {
  --section-header-margin: var(--sp-6) var(--sp-6) var(--sp-3);

  .shipment-basic-info-section-data {
    padding: 0 var(--sp-3) var(--sp-3);

    .rygen-message {
      margin-bottom: var(--sp-3);
    }

    .row {
      display: flex;

      .shipment-basic-info-cell {
        padding: var(--sp-3);
        flex: 1 0 auto;
        width: 20%;

        .popup-inputgroup {
          & > .popup-inputgroup-card {
            width: max-content;
          }
        }
      }
    }
  }
}
</style>
